import { z } from 'zod';

/**
 * Block library — BACKEND-SPEC §1.7.
 *
 * `content_json` is validated per kind on every write. A block whose payload does not
 * match its kind can never reach `page_blocks`, so the renderer never has to defend
 * against shapes that "should not happen".
 */

export const BLOCK_KINDS = [
  'hero_cta',
  'bonus_banner',
  'banner_grid',
  'bonus_table',
  'ai_text',
  'manual_text',
  'heading',
  'game_grid',
  'game_cards',
  'providers',
  'payment_methods',
  'top_brands_table',
  'steps',
  'faq',
  'license_rg',
  'socials',
  'breadcrumbs',
  'internal_links',
  'specs_table',
  'screens_gallery',
  'quote',
  'comparison_table',
  'image',
  'cta_button',
  'html_embed',
] as const;

export type BlockKind = (typeof BLOCK_KINDS)[number];

export const BLOCK_KIND_LABELS: Record<BlockKind, string> = {
  hero_cta: 'Hero + CTA',
  bonus_banner: 'Бонус-банер',
  banner_grid: 'Сітка банерів',
  bonus_table: 'Таблиця бонусів',
  ai_text: 'AI-текст',
  manual_text: 'Ручний текст',
  heading: 'Заголовок',
  game_grid: 'Ігрова сітка',
  game_cards: 'Картки ігор',
  providers: 'Провайдери',
  payment_methods: 'Платіжні методи',
  top_brands_table: 'Топ брендів',
  steps: 'Кроки',
  faq: 'FAQ',
  license_rg: 'Ліцензія та RG',
  socials: 'Соцмережі',
  breadcrumbs: 'Хлібні крихти',
  internal_links: 'Перелінковка',
  specs_table: 'Таблиця характеристик',
  screens_gallery: 'Галерея скріншотів',
  quote: 'Цитата',
  comparison_table: 'Порівняльна таблиця',
  image: 'Зображення',
  cta_button: 'CTA-кнопка',
  html_embed: 'HTML-вставка',
};

/** Kinds whose body is produced by an LLM; everything else resolves from entities. */
export const AI_BLOCK_KINDS: readonly BlockKind[] = ['ai_text'] as const;

/** Kinds that are meaningful even with no rows — never auto-hidden. */
export const ALWAYS_VISIBLE_BLOCK_KINDS: readonly BlockKind[] = ['breadcrumbs'] as const;

/**
 * Поле, яке рендерер бере з блока як редагований текст, — або `null`, якщо вміст цього виду
 * збирається зі структури, а не з рядка.
 *
 * Існує через мовчазний нуль: редактор умів додавати рівно FAQ-блок і клав у нього
 * `{ text: 'Новий блок' }`, а `renderBlock` для `faq` читає `items` і на порожньому переліку
 * повертає порожній рядок. Тобто єдиний блок, який можна було додати з інтерфейсу, ніколи не
 * потрапляв у HTML — без помилки, без попередження, без сліду в збірці.
 *
 * Мапа знята з `renderBlock` і мусить лишатися його дзеркалом — це перевіряється прогоном, а
 * не звіркою очима (`block-text-field.test.ts`): кожен вид рендериться зондом у полі, яке мапа
 * обіцяє, і зонд або зʼявляється в розмітці, або ні.
 *
 * 15.08 мапа виявилась брехливою на **шістнадцяти** видах із двадцяти чотирьох, і брехня була
 * тонка: `'text'` там означало «запасна гілка рендерера друкує `data.text`» — а схема вмісту
 * цього поля не має взагалі й **стирає** його на записі. Тобто текст, який редактор туди
 * поклав, не доїжджав навіть до бази, не те що до HTML.
 *
 * Гейт цього не бачив, бо годував рендерер обʼєктом, зліпленим руками: `{ text: зонд }` ніколи
 * не проходив `blockContentSchemas`. Тепер проходить — і мапа каже правду: текстове поле мають
 * рівно три види, решта збирається зі структури й має власні гілки в рендерері.
 */
export const BLOCK_TEXT_FIELD: Record<BlockKind, 'text' | 'heading' | null> = {
  hero_cta: 'heading',
  bonus_banner: null,
  banner_grid: null,
  bonus_table: null,
  ai_text: 'text',
  manual_text: 'text',
  heading: 'text',
  game_grid: null,
  game_cards: null,
  providers: null,
  payment_methods: null,
  top_brands_table: null,
  steps: null,
  faq: null,
  license_rg: null,
  socials: null,
  breadcrumbs: null,
  internal_links: null,
  specs_table: null,
  screens_gallery: null,
  quote: 'text',
  comparison_table: null,
  image: null,
  cta_button: null,
  html_embed: null,
};

// ── shared fragments ──────────────────────────────────────────────────────────────

const mediaRef = z.object({
  mediaFileId: z.uuid().nullable(),
  url: z.string().nullable(),
  alt: z.string().default(''),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});

const tiptapMarkSchema = z.object({
  type: z.string().min(1),
  attrs: z.record(z.string(), z.unknown()).optional(),
});

/** Recursive TipTap v3 JSON node. Raw HTML and arbitrary non-document values are rejected. */
export const tiptapNodeSchema: z.ZodType = z.lazy(() =>
  z.object({
    type: z.string().min(1),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(tiptapNodeSchema).optional(),
    marks: z.array(tiptapMarkSchema).optional(),
    text: z.string().optional(),
  }),
);

export const richTextSchema = z
  .object({
    /** TipTap JSON document; never raw HTML. */
    doc: z.object({ type: z.literal('doc'), content: z.array(tiptapNodeSchema).default([]) }),
    plain: z.string(),
    words: z.number().int().nonnegative(),
  })
  .superRefine((value, check) => {
    const words = value.plain.trim() === '' ? 0 : value.plain.trim().split(/\s+/u).length;
    if (value.words !== words) {
      check.addIssue({
        code: 'custom',
        path: ['words'],
        message: `expected ${String(words)} words for plain text`,
      });
    }
  });

export type RichText = z.infer<typeof richTextSchema>;

/** Builds the same structured payload the editor and the LLM boundary persist. */
export function richTextFromPlain(plain: string): RichText {
  const trimmed = plain.trim();
  const words = trimmed === '' ? 0 : trimmed.split(/\s+/u).length;
  return {
    doc: {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: plain === '' ? [] : [{ type: 'text', text: plain }],
        },
      ],
    },
    plain,
    words,
  };
}

const ctaSchema = z.object({
  label: z.string(),
  href: z.string(),
  /** Resolved from `sites.offer_link_id` at assemble time when true. */
  useOfferLink: z.boolean().default(true),
  nofollow: z.boolean().default(true),
});

// ── per-kind content schemas ──────────────────────────────────────────────────────

export const blockContentSchemas = {
  hero_cta: z.strictObject({
    heading: z.string(),
    subheading: z.string().default(''),
    background: mediaRef.nullable().default(null),
    logo: mediaRef.nullable().default(null),
    badges: z.array(z.string()).default([]),
    cta: ctaSchema,
  }),

  bonus_banner: z.strictObject({
    bonusId: z.uuid().nullable(),
    title: z.string(),
    valueText: z.string(),
    terms: z.string().default(''),
    image: mediaRef.nullable().default(null),
    cta: ctaSchema,
  }),

  banner_grid: z.strictObject({
    columns: z.number().int().min(1).max(6).default(3),
    items: z
      .array(
        z.object({
          image: mediaRef,
          title: z.string().default(''),
          href: z.string().default(''),
        }),
      )
      .default([]),
  }),

  bonus_table: z.strictObject({
    columns: z.array(z.string()).default([]),
    rows: z
      .array(
        z.object({
          bonusId: z.uuid().nullable(),
          type: z.string(),
          value: z.string(),
          wagering: z.string().default(''),
          minDeposit: z.string().default(''),
          validDays: z.string().default(''),
          terms: z.string().default(''),
        }),
      )
      .default([]),
  }),

  ai_text: z.strictObject({
    text: richTextSchema,
    promptProfileId: z.uuid().nullable(),
    promptVersion: z.number().int().nullable(),
  }),

  manual_text: z.strictObject({ text: richTextSchema }),

  heading: z.strictObject({
    level: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(2),
    text: z.string(),
  }),

  game_grid: z.strictObject({
    layout: z.enum(['grid', 'carousel']).default('grid'),
    columns: z.number().int().min(2).max(8).default(6),
    tabs: z.array(z.string()).default([]),
    games: z
      .array(
        z.object({
          gameId: z.uuid(),
          name: z.string(),
          providerName: z.string().default(''),
          thumbnail: mediaRef.nullable().default(null),
          href: z.string().default(''),
        }),
      )
      .default([]),
  }),

  game_cards: z.strictObject({
    games: z
      .array(
        z.object({
          gameId: z.uuid(),
          name: z.string(),
          providerName: z.string().default(''),
          rtp: z.string().default(''),
          volatility: z.string().default(''),
          maxWin: z.string().default(''),
          image: mediaRef.nullable().default(null),
          href: z.string().default(''),
        }),
      )
      .default([]),
  }),

  providers: z.strictObject({
    items: z
      .array(
        z.object({
          providerId: z.uuid(),
          name: z.string(),
          logo: mediaRef.nullable().default(null),
          href: z.string().default(''),
        }),
      )
      .default([]),
  }),

  payment_methods: z.strictObject({
    groupByType: z.boolean().default(true),
    items: z
      .array(
        z.object({
          paymentMethodId: z.uuid(),
          name: z.string(),
          type: z.string(),
          logo: mediaRef,
          minDeposit: z.string().default(''),
          maxDeposit: z.string().default(''),
          processingTime: z.string().default(''),
          withdrawalSupported: z.boolean().default(true),
        }),
      )
      .default([]),
  }),

  top_brands_table: z.strictObject({
    rows: z
      .array(
        z.object({
          position: z.number().int().positive(),
          brandId: z.uuid(),
          brandName: z.string(),
          logo: mediaRef.nullable().default(null),
          bonusText: z.string().default(''),
          rating: z.number().min(0).max(5).nullable().default(null),
          cta: ctaSchema,
        }),
      )
      .default([]),
  }),

  steps: z.strictObject({
    items: z.array(z.object({ title: z.string(), body: z.string().default('') })).default([]),
  }),

  faq: z.strictObject({
    items: z.array(z.object({ q: z.string(), a: z.string() })).default([]),
    schemaOrg: z.boolean().default(true),
  }),

  license_rg: z.strictObject({
    licenseId: z.uuid().nullable(),
    licenseText: z.string().default(''),
    licenseNumber: z.string().default(''),
    badge: mediaRef.nullable().default(null),
    minAge: z.number().int().nullable().default(null),
    disclaimer: z.string().default(''),
    organisations: z
      .array(
        z.object({
          rgOrganizationId: z.uuid(),
          name: z.string(),
          url: z.string().default(''),
          helpline: z.string().default(''),
          icon: mediaRef.nullable().default(null),
        }),
      )
      .default([]),
  }),

  socials: z.strictObject({
    items: z.array(z.object({ network: z.string(), url: z.string() })).default([]),
  }),

  breadcrumbs: z.strictObject({
    items: z.array(z.object({ label: z.string(), href: z.string() })).default([]),
    schemaOrg: z.boolean().default(true),
  }),

  internal_links: z.strictObject({
    title: z.string().default(''),
    items: z
      .array(z.object({ label: z.string(), href: z.string(), pageId: z.uuid().nullable() }))
      .default([]),
  }),

  specs_table: z.strictObject({
    rows: z.array(z.object({ k: z.string(), v: z.string() })).default([]),
  }),

  screens_gallery: z.strictObject({
    items: z.array(mediaRef).default([]),
  }),

  quote: z.strictObject({
    text: z.string(),
    author: z.string().default(''),
  }),

  comparison_table: z.strictObject({
    columns: z.array(z.string()).default([]),
    rows: z.array(z.array(z.string())).default([]),
  }),

  image: z.strictObject({ image: mediaRef.nullable(), caption: z.string().default('') }),

  cta_button: z.strictObject({
    cta: ctaSchema.nullable(),
    align: z.enum(['left', 'center', 'right']).default('center'),
  }),

  html_embed: z.strictObject({ html: z.string().default('') }),
} as const satisfies Record<BlockKind, z.ZodType>;

export type BlockContentSchemas = typeof blockContentSchemas;

export type BlockContent<K extends BlockKind = BlockKind> = z.infer<BlockContentSchemas[K]>;

export function blockContentSchemaFor(kind: BlockKind): z.ZodType {
  return blockContentSchemas[kind];
}

/**
 * Transport contract for manual writes. The discriminator selects the payload schema before
 * the request reaches a service; the service parses again against the persisted block kind.
 */
export const blockContentWriteSchema = z.discriminatedUnion('blockKind', [
  z.object({ blockKind: z.literal('hero_cta'), content: blockContentSchemas.hero_cta }),
  z.object({ blockKind: z.literal('bonus_banner'), content: blockContentSchemas.bonus_banner }),
  z.object({ blockKind: z.literal('banner_grid'), content: blockContentSchemas.banner_grid }),
  z.object({ blockKind: z.literal('bonus_table'), content: blockContentSchemas.bonus_table }),
  z.object({ blockKind: z.literal('ai_text'), content: blockContentSchemas.ai_text }),
  z.object({ blockKind: z.literal('manual_text'), content: blockContentSchemas.manual_text }),
  z.object({ blockKind: z.literal('heading'), content: blockContentSchemas.heading }),
  z.object({ blockKind: z.literal('game_grid'), content: blockContentSchemas.game_grid }),
  z.object({ blockKind: z.literal('game_cards'), content: blockContentSchemas.game_cards }),
  z.object({ blockKind: z.literal('providers'), content: blockContentSchemas.providers }),
  z.object({
    blockKind: z.literal('payment_methods'),
    content: blockContentSchemas.payment_methods,
  }),
  z.object({
    blockKind: z.literal('top_brands_table'),
    content: blockContentSchemas.top_brands_table,
  }),
  z.object({ blockKind: z.literal('steps'), content: blockContentSchemas.steps }),
  z.object({ blockKind: z.literal('faq'), content: blockContentSchemas.faq }),
  z.object({ blockKind: z.literal('license_rg'), content: blockContentSchemas.license_rg }),
  z.object({ blockKind: z.literal('socials'), content: blockContentSchemas.socials }),
  z.object({ blockKind: z.literal('breadcrumbs'), content: blockContentSchemas.breadcrumbs }),
  z.object({ blockKind: z.literal('internal_links'), content: blockContentSchemas.internal_links }),
  z.object({ blockKind: z.literal('specs_table'), content: blockContentSchemas.specs_table }),
  z.object({
    blockKind: z.literal('screens_gallery'),
    content: blockContentSchemas.screens_gallery,
  }),
  z.object({ blockKind: z.literal('quote'), content: blockContentSchemas.quote }),
  z.object({
    blockKind: z.literal('comparison_table'),
    content: blockContentSchemas.comparison_table,
  }),
  z.object({ blockKind: z.literal('image'), content: blockContentSchemas.image }),
  z.object({ blockKind: z.literal('cta_button'), content: blockContentSchemas.cta_button }),
  z.object({ blockKind: z.literal('html_embed'), content: blockContentSchemas.html_embed }),
]);

export type BlockContentWrite = z.infer<typeof blockContentWriteSchema>;

const EMPTY_BLOCK_CONTENT: { [K in BlockKind]: z.input<BlockContentSchemas[K]> } = {
  hero_cta: { heading: '', cta: { label: '', href: '' } },
  bonus_banner: { bonusId: null, title: '', valueText: '', cta: { label: '', href: '' } },
  banner_grid: { items: [] },
  bonus_table: { rows: [] },
  ai_text: { text: richTextFromPlain(''), promptProfileId: null, promptVersion: null },
  manual_text: { text: richTextFromPlain('') },
  heading: { text: '' },
  game_grid: { games: [] },
  game_cards: { games: [] },
  providers: { items: [] },
  payment_methods: { items: [] },
  top_brands_table: { rows: [] },
  steps: { items: [] },
  faq: { items: [] },
  license_rg: { licenseId: null },
  socials: { items: [] },
  breadcrumbs: { items: [] },
  internal_links: { items: [] },
  specs_table: { rows: [] },
  screens_gallery: { items: [] },
  quote: { text: '' },
  comparison_table: { rows: [] },
  image: { image: null },
  cta_button: { cta: null },
  html_embed: {},
};

/** A newly materialised block is empty but already valid for its discriminator. */
export function emptyBlockContent(kind: BlockKind): unknown {
  return blockContentSchemas[kind].parse(EMPTY_BLOCK_CONTENT[kind]);
}

/** Parses `content_json` against its kind. Throws `ZodError` — callers map to AppError. */
export function parseBlockContent(kind: BlockKind, value: unknown): unknown {
  return blockContentSchemas[kind].parse(value);
}

/**
 * Emptiness test used by the `hide_empty_blocks` template rule (§2 step 6).
 * A block that resolves to nothing becomes `hidden`, never an empty section.
 */
export function isBlockContentEmpty(kind: BlockKind, content: unknown): boolean {
  if (content === null || typeof content !== 'object') return true;
  const c = content as Record<string, unknown>;

  const arrayFields = ['items', 'rows', 'games', 'badges'] as const;
  for (const f of arrayFields) {
    if (Array.isArray(c[f])) return (c[f] as unknown[]).length === 0;
  }

  switch (kind) {
    case 'ai_text':
    case 'manual_text': {
      const text = c.text as { plain?: string } | undefined;
      return !text?.plain || text.plain.trim().length === 0;
    }
    case 'heading':
    case 'quote':
      return typeof c.text !== 'string' || c.text.trim().length === 0;
    case 'hero_cta':
      return typeof c.heading !== 'string' || c.heading.trim().length === 0;
    case 'bonus_banner':
      return typeof c.valueText !== 'string' || c.valueText.trim().length === 0;
    case 'html_embed':
      return typeof c.html !== 'string' || c.html.trim().length === 0;
    case 'image':
      return c.image === null || c.image === undefined;
    case 'license_rg':
      return !c.licenseText && !c.disclaimer;
    case 'cta_button':
      return c.cta === null || c.cta === undefined;
    default:
      return false;
  }
}
